import { useEffect, useMemo, useRef } from 'react';
import { debounce } from './debounce.ts';

export type ThrottledFunction = (...args: unknown[]) => void;

/**
 * Returns a throttled version of the given function.
 *
 * @param callback  the function to be throttled
 * @param wait      throttle interval in ms
 * @param trailing  if a trailing call should be made if there were 1+ calls in the throttling interval
 * @returns         the throttled function
 */
export const throttle = <T extends (...args: any) => void>(
  callback: T,
  wait: number,
  trailing: boolean = true
): ((...args: Parameters<T>) => void) => {
  let lastCall: number | undefined;
  let numberOfUncalled: number = 0;

  // A trailing call is simply a debounced one, which checks if there are missing calls.
  const debounced = trailing
    ? debounce((...args: Parameters<T>[]) => {
        if (numberOfUncalled) {
          callback(...args);
          numberOfUncalled = 0;
        }
      }, wait)
    : undefined;

  return (...args: Parameters<T>[]): void => {
    const now = Date.now();
    if (!lastCall || now - wait > lastCall) {
      callback(...args);
      lastCall = now;
      numberOfUncalled = 0;
    } else {
      numberOfUncalled++;
    }

    debounced?.(...args);
  };
};

/**
 * Hook to easier throttle functions in functional components.
 */
export const useThrottle = <T extends (...args: any) => void>(
  callback: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const throttledCallback = useMemo(
    () => throttle((...args: Parameters<T>[]) => callbackRef.current(...args), wait),
    [wait]
  );

  return throttledCallback;
};
